import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { githubApi } from '../lib/api-client';
import { friendlyErrorMessage } from '../components/ui/states';
import { GITHUB_INSTALL_STATE_KEY } from '../lib/github-install-state';
import { SkeletonRows } from '../components/ui/skeleton';

type Outcome = { kind: 'pending' } | { kind: 'error'; message: string };

/**
 * Mounted at the SPA route GitHub's App install/authorization flow redirects back to (see
 * apps/api's docs/github-enterprise.md "Installation flow" + packages/github/src/config.ts's
 * `buildAppManifest`'s `redirect_url`). Finishes the flow by POSTing the installation id, OAuth
 * code/state, and the `installState` stashed in sessionStorage by GitHubEnterprise.tsx to
 * /v1/integrations/github/install/callback — same "SPA lands the redirect, then calls a JSON
 * API" shape as SsoCallback.tsx. This route must be Protected (unlike SsoCallback) — the caller
 * needs to already be signed in, since install/callback is authenticated the same as every other
 * githubIntegration:manage route.
 */
export function GitHubCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'pending' });

  useEffect(() => {
    const installationId = params.get('installation_id');
    const code = params.get('code');
    const state = params.get('state');
    const installState = sessionStorage.getItem(GITHUB_INSTALL_STATE_KEY);
    sessionStorage.removeItem(GITHUB_INSTALL_STATE_KEY);

    if (!installationId || !code || !state || !installState) {
      setOutcome({
        kind: 'error',
        message: 'This GitHub connection link is missing required information. Please try connecting again from the GitHub Enterprise page.',
      });
      return;
    }

    githubApi
      .installCallback({ installationId, code, state, installState })
      .then(() => navigate('/github', { replace: true }))
      .catch((err: unknown) => setOutcome({ kind: 'error', message: friendlyErrorMessage(err) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (outcome.kind === 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-4 text-center">
          <p className="text-sm text-ink-muted">Finishing GitHub connection…</p>
          <SkeletonRows rows={2} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6 text-center">
      <p className="text-sm text-danger">{outcome.message}</p>
    </div>
  );
}
